import {
  createAsyncThunk,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit'
import {
  setBuildingDocument,
  setConvertData,
  updateConvertData,
} from '../actions/construction'
import { BuildingDocumentType, ConvertData } from '../models/Construction'
import { setConvertFailedError, setIsLoading } from '../actions/files'
import { RootState } from '../store'
import { getIDToken } from '../utils/AuthUtil'
import { BASE_URL, S3_BUCKET_NAME } from '../config'
import { resetUpdatedTime, setHistory, updateHistory } from '../actions/history'
import { splitS3FilePath } from '../utils/ServiceUtil'
import {
  ConvertFailedErrorType,
  DrawingFile,
  LoadingPage,
} from '../models/File'
import { pdfPagesNumber, pdfToImage } from '../utils/PdfUtil'
import { v4 as uuidv4 } from 'uuid'
import { Dispatch, SetStateAction } from 'react'
import { ActionType } from '../reducers/history'
import { filterBuildingByIds } from '../utils/ConstructionUtil'
import { SceneManager } from '../visualization/sceneManager'
import { EventType } from '../utils/EventEmitter'
import { SelectionManager } from '../visualization/selection/selectionManager'

export const fetchBuildingDocument = createAsyncThunk(
  'building/fetchBuildingInfo',
  async (
    payload: { filePath: string; doc_type: BuildingDocumentType },
    { dispatch, getState },
  ) => {
    const { s3_folder, s3_file } = splitS3FilePath(payload.filePath)
    const requestBody = {
      s3_bucketname: S3_BUCKET_NAME,
      s3_folder,
      s3_file,
      doc_type: payload.doc_type.toString(),
    }

    try {
      const response = await fetch(`${BASE_URL}/api/building/documents`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const documentInfo = (await response.json()) as {
        s3Key: string
        url: string
      }
      if (documentInfo.url) {
        const response = await fetch(documentInfo.url, {
          method: 'GET',
          headers: {
            'Access-Control-Allow-Origin': '*',
          },
        })

        if (!response.ok) {
          throw new Error(`Server Error: ${response.status}`)
        }

        const blob = await response.blob()
        loadBuildingDocument(
          {
            data: new File([blob], documentInfo.s3Key, {
              type: 'application/pdf',
            }),
            type: payload.doc_type,
            url: response.url,
          },
          dispatch,
        )
      }
    } catch (error: any) {
      throw error
    }
  },
)

export const fetchBuildingInfo = createAsyncThunk(
  'building/fetchBuildingInfo',
  async (
    payload: { fileKey: string; withRetry?: boolean },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const requestBody = { fileKey: payload.fileKey }

    try {
      const response = await fetch(`${BASE_URL}/api/building/`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const convertData: ConvertData = await response.json()
      const buildingHasAllElements =
        convertData.building.has_elements &&
        Object.values(convertData.building.has_elements).every(
          (value) => !!value,
        )
      if (
        !!convertData.building &&
        !!convertData.building.storeys &&
        !!convertData.building.storeys.length &&
        !!convertData.building.storeys.some((storey) => storey.walls)
      ) {
        for (let i = 0; i < convertData.building.storeys.length; i++) {
          const storey = convertData.building.storeys[i]
          storey.image = state.drawings.recognizedPages[i]
            ? state.drawings.recognizedPages[i].image
            : storey.image || ''
        }

        dispatch(setIsLoading(false))
        dispatch(setConvertData(convertData))
        dispatch(setHistory({ convertData }))
      } else if (
        !!convertData.building &&
        buildingHasAllElements &&
        (!!!convertData.building.storeys ||
          !!!convertData.building.storeys.some((storey) => storey.walls))
      ) {
        dispatch(setConvertFailedError(ConvertFailedErrorType.SystemError))
      } else {
        throw new Error('Response is empty')
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        // console.error('Error while fetching building info: ', error)
      }

      if (payload.withRetry) {
        throw error
      }
    }
  },
)

export const updateBuildingInfo = createAsyncThunk(
  'building/updateBuildingInfo',
  async (
    payload: {
      fileKey: string
      building_json: ConvertData
      elementId: string | null
      silent?: boolean
    },
    { dispatch, getState },
  ) => {
    try {
      const { fileKey, building_json, elementId, silent } = payload
      const response = await fetch(`${BASE_URL}/api/building/`, {
        method: 'PUT',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileKey,
          building_json,
          elementId,
        }),
      })

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const convertData: ConvertData = await response.json()
      if (silent) {
        const state = getState() as RootState
        convertData.context_id = state.construction.convertData.context_id
      }

      dispatch(
        updateConvertData({
          convertData,
          elementId: payload.elementId,
        }),
      )
      dispatch(resetUpdatedTime())
    } catch (error: any) {
      throw error
    }
  },
)

export const updateBuildingElementInfo = createAsyncThunk(
  'building/updateBuildingElementInfo',
  async (
    payload: {
      filepath: string | undefined
      data: any
      elementId: string
      silent?: boolean
    },
    { dispatch, getState },
  ) => {
    try {
      const { filepath, data, elementId, silent } = payload

      if (!filepath) {
        console.warn('Selected file is undefined.')
        return
      }

      const { s3_file, s3_folder } = splitS3FilePath(filepath)
      const response = await fetch(
        `${BASE_URL}/api/building/elements/${elementId}`,
        {
          method: 'PATCH',
          headers: {
            'Access-Control-Allow-Origin': '*',
            Authorization: `Bearer ${await getIDToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            s3File: s3_file,
            s3Folder: s3_folder,
            s3Bucketname: S3_BUCKET_NAME,
            data,
          }),
        },
      )

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const convertData: ConvertData = await response.json()
      if (silent) {
        const state = getState() as RootState
        convertData.context_id = state.construction.convertData.context_id
      }

      dispatch(
        updateConvertData({
          convertData,
          elementId: payload.elementId,
        }),
      )
      dispatch(resetUpdatedTime())
    } catch (error: any) {
      throw error
    }
  },
)

type SeparateWallResponse = {
  addedWallIds: string[]
  removedWallIds: string[]
  buildingJson: ConvertData
}

export const separateWall = createAsyncThunk(
  'building/separateWall',
  async (
    payload: {
      wallId: string
      splitPoint: number
      signal?: AbortSignal
      setIsLoading?: Dispatch<SetStateAction<boolean>>
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const filepath = state.organization.selectedFile?.path
    if (!filepath) {
      console.warn('Selected file is undefined.')
      return
    }

    const { s3_file, s3_folder } = splitS3FilePath(filepath)
    const { wallId, splitPoint, signal, setIsLoading } = payload
    const body = {
      wallId,
      splitPoint,
      s3File: s3_file,
      s3Folder: s3_folder,
      s3Bucketname: S3_BUCKET_NAME,
    }

    try {
      setIsLoading?.(true)

      const response = await fetch(`${BASE_URL}/api/building/walls/split`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      })

      if (!response.ok) {
        throw new Error(`Server Error: ${response.status}`)
      }

      const {
        removedWallIds,
        addedWallIds,
        buildingJson,
      }: SeparateWallResponse = await response.json()

      const deletedItems = removedWallIds.map((id) => {
        return {
          itemId: id,
          actionType: ActionType.DELETE,
        }
      })

      const addedItems = addedWallIds.map((id) => {
        return {
          itemId: id,
          actionType: ActionType.ADD,
        }
      })

      dispatch(
        updateHistory({
          convertData: buildingJson,
          items: [...deletedItems, ...addedItems],
        }),
      )

      // Update building info with generated building json
      await dispatch(
        updateBuildingInfo({
          fileKey: buildingJson.json_s3_key,
          building_json: buildingJson,
          elementId: null,
          silent: true,
        }),
      )

      const selectionManager = SelectionManager.getInstance()
      selectionManager.deselect()

      const filteredBuilding = filterBuildingByIds(
        buildingJson.building,
        addedWallIds,
      )
      const eventEmitter = SceneManager.getInstance().getEventEmitter()
      eventEmitter?.emit(EventType.BuildingCreateBuilding, {
        building: filteredBuilding,
        removedWallIds,
        addedWallIds,
      })
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error(
          `Error while separating the wall with id ${wallId} at ${splitPoint}: `,
          {
            wallId,
            splitPoint,
            error,
          },
        )

        throw error
      }
    } finally {
      setIsLoading?.(false)
    }
  },
)

const loadBuildingDocument = (
  payload: { data: File; url: string; type: BuildingDocumentType },
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
) => {
  return new Promise<DrawingFile>(async (resolve, reject) => {
    const fileReader = new FileReader()
    const fileId = uuidv4()
    const pages: LoadingPage[] = []

    fileReader.onload = async () => {
      try {
        const pdfData = fileReader.result as string
        const numPages = await pdfPagesNumber(pdfData)
        for (let i = 1; i < numPages + 1; i++) {
          const pageId = uuidv4()
          pages.push({
            id: pageId,
            number: i,
            image: '',
            isLoading: true,
            filename: payload.data.name,
          })
        }

        dispatch(
          setBuildingDocument({
            file: {
              id: fileId,
              name: payload.data.name,
              pages,
              url: payload.url,
            },
            type: payload.type,
            selectedPageId: pages[0].id,
          }),
        )

        pdfToImage(fileId, pages, pdfData, dispatch)
      } catch (error) {
        console.error(error)
      }
    }

    fileReader.onerror = (error) => {
      reject(error)
    }

    fileReader.readAsArrayBuffer(payload.data)
  })
}
